import { getClassLevel } from '../../classIdentity.mjs';

// 2024 Oath of Devotion (SRD 5.2). Oath spells are always prepared through feature grants (no free casts);
// Sacred Weapon spends a Channel Divinity use. Aura of Devotion (7) rides on the Aura of Protection projection
// (server/utils/auras.js), as do Smite of Protection (15, Half Cover after Divine Smite) and Holy Nimbus (20).
export const DEVOTION_SUBCLASS_ID = 'devotion';
export const SACRED_WEAPON_FEATURE_ID = 'paladin:devotion:sacred-weapon';
export const SACRED_WEAPON_EFFECT_ID = 'paladin:devotion:sacred-weapon';
export const OATH_SPELL_GRANT_PREFIX = 'paladin-devotion-';
/** Oath of Devotion spells by Paladin level. */
export const DEVOTION_OATH_SPELLS = Object.freeze({
  3: Object.freeze(['protection-from-evil-and-good', 'shield-of-faith']),
  5: Object.freeze(['aid', 'zone-of-truth']),
  9: Object.freeze(['beacon-of-hope', 'dispel-magic']),
  13: Object.freeze(['freedom-of-movement', 'guardian-of-faith']),
  17: Object.freeze(['commune', 'flame-strike']),
});

const isDevotion = character => {
  const state = character?.classState?.paladin;
  const raw = state?.subclassId ?? state?.subclass?.id ?? (typeof state?.subclass === 'string' ? state.subclass : null);
  return String(raw || '').trim().toLowerCase() === DEVOTION_SUBCLASS_ID;
};

/** Oath spells this Paladin has prepared through the Oath of Devotion, lowest level first. */
export const getDevotionOathSpells = character => {
  if (!isDevotion(character)) return [];
  const level = getClassLevel(character, 'paladin');
  return Object.entries(DEVOTION_OATH_SPELLS).filter(([gained]) => level >= Number(gained)).flatMap(([, spells]) => spells);
};
export const oathSpellGrantId = spellId => `${OATH_SPELL_GRANT_PREFIX}${spellId}`;
export const oathSpellForGrant = grantId => (String(grantId || '').startsWith(OATH_SPELL_GRANT_PREFIX) ? String(grantId).slice(OATH_SPELL_GRANT_PREFIX.length) : null);

/** The active Sacred Weapon effect this Paladin started, or null. */
export const getSacredWeaponEffect = (activeEffects, combatantId) => (Array.isArray(activeEffects) ? activeEffects : [])
  .find(effect => effect?.definitionId === SACRED_WEAPON_EFFECT_ID && String(effect.sourceCombatantId) === String(combatantId)) || null;

/** Sacred Weapon adds the Charisma modifier to attack rolls with the imbued weapon, with a minimum bonus of +1. */
export const sacredWeaponAttackBonus = charismaModifier => Math.max(1, Math.floor(Number(charismaModifier) || 0));

/** The weapon's damage dealt as Radiant instead of its normal type, e.g. "1d8 slashing" -> "1d8 radiant". */
export const sacredWeaponDamage = damage => {
  const text = String(damage || '');
  return /\b(bludgeoning|piercing|slashing)\b/i.test(text) ? text.replace(/\b(bludgeoning|piercing|slashing)\b/i, 'radiant') : `${text} radiant`.trim();
};

const devotion = Object.freeze({
  id: DEVOTION_SUBCLASS_ID, definitionId: 'paladin:devotion', classId: 'paladin',
  name: 'Oath of Devotion', selectionLevel: 3,
  description: 'Uphold the ideals of justice and order.',
  features: Object.freeze([
    {
      id: 'paladin:devotion:oath-spells', name: 'Oath of Devotion Spells', level: 3,
      description: 'The magic of your oath ensures you always have certain spells ready; when you reach a Paladin level specified in the Oath of Devotion Spells table, you thereafter always have the listed spells prepared: Protection from Evil and Good and Shield of Faith (3), Aid and Zone of Truth (5).',
      presentation: { kind: 'passive' }, contracts: [],
    },
    {
      id: SACRED_WEAPON_FEATURE_ID, name: 'Sacred Weapon', level: 3,
      description: 'When you take the Attack action, you can expend one use of your Channel Divinity to imbue one Melee weapon that you are holding with positive energy. For 10 minutes or until you use this feature again, you add your Charisma modifier to attack rolls you make with that weapon (minimum bonus of +1), and each time you hit with it, you can cause it to deal its normal damage type or Radiant damage. The weapon also emits Bright Light in a 20-foot radius and Dim Light 20 feet beyond that. You can end this effect early (no action required). This effect also ends if you aren’t carrying the weapon.',
      presentation: { kind: 'bonus-action' }, contracts: [],
    },
    {
      id: 'paladin:devotion:aura-of-devotion', name: 'Aura of Devotion', level: 7,
      description: 'You and your allies have Immunity to the Charmed condition while in your Aura of Protection. If a Charmed ally enters the aura, that condition has no effect on that ally while there.',
      presentation: { kind: 'passive' }, contracts: [],
    },
    {
      id: 'paladin:devotion:smite-of-protection', name: 'Smite of Protection', level: 15,
      description: 'Your magical smite now radiates protective energy. Whenever you cast Divine Smite, you and your allies have Half Cover while in your Aura of Protection. The aura has this benefit until the start of your next turn.',
      presentation: { kind: 'passive' }, contracts: [],
    },
    {
      id: 'paladin:devotion:holy-nimbus', name: 'Holy Nimbus', level: 20,
      description: 'As a Bonus Action, you can imbue your Aura of Protection with holy power, granting the benefits below for 10 minutes or until you end them (no action required). Once you use this feature, you can’t use it again until you finish a Long Rest. You can also restore your use of it by expending a level 5 spell slot (no action required).',
      presentation: { kind: 'bonus-action' }, contracts: [],
    },
  ]),
});

export default devotion;
